import { apiClient } from "./apiClient";

export type EntitlementValue = boolean | number | null;

export interface CommercialContext {
  account_kind: "provider_organization" | "individual_care_coordinator";
  offer_family: "provider" | "care_coordination";
  active_offers: string[];
  materialization_status: "pending" | "ready" | "error";
  entitlements_revision: number;
  entitlements: Record<string, EntitlementValue>;
}

export async function fetchCommercialContext(
  signal?: AbortSignal
): Promise<CommercialContext> {
  const response = await apiClient.get<CommercialContext>(
    "/commercial/context",
    { signal }
  );
  return response.data;
}

export interface CommercialUsageItem {
  key: string;
  kind: "capacity" | "monthly_meter";
  used: number;
  limit: number | null;
  remaining: number | null;
  period_start: string | null;
  resets_at: string | null;
}

export interface CommercialUsage {
  items: CommercialUsageItem[];
  measured_at: string;
}

export async function fetchCommercialUsage(
  signal?: AbortSignal
): Promise<CommercialUsage> {
  const response = await apiClient.get<CommercialUsage>("/commercial/usage", {
    signal,
  });
  return response.data;
}

export interface CommercialBillingStatus {
  /** Backward-compatible alias for checkout_enabled. */
  self_service_enabled: boolean;
  checkout_enabled: boolean;
  portal_enabled: boolean;
  can_manage_subscription: boolean;
  /** Optional only for compatibility during a rolling deployment from the pre-state API. */
  upgrade_state?:
    | "coming_soon"
    | "request_access"
    | "checkout_available"
    | "owner_action_required";
  offer_family: "provider" | "care_coordination";
  available_offers: Array<{
    offer_version: string;
    interval: "month" | "year";
  }>;
  subscription_status: string | null;
  cancel_at_period_end: boolean;
  current_period_ends_at: string | null;
  access_grace_ends_at: string | null;
}

export async function fetchCommercialBillingStatus(
  signal?: AbortSignal
): Promise<CommercialBillingStatus> {
  const response = await apiClient.get<CommercialBillingStatus>(
    "/commercial/billing",
    { signal }
  );
  return response.data;
}

export async function createCommercialCheckout(
  offerVersion: string,
  interval: "month" | "year",
  idempotencyKey: string
): Promise<string> {
  const response = await apiClient.post<{ url: string }>(
    "/commercial/billing/checkout",
    { offer_version: offerVersion, interval },
    { headers: { "Idempotency-Key": idempotencyKey } }
  );
  return response.data.url;
}

export async function createCommercialBillingPortal(): Promise<string> {
  const response = await apiClient.post<{ url: string }>(
    "/commercial/billing/portal"
  );
  return response.data.url;
}
